import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { Card } from "@/components/ui/Card"
import { formatDate, formatValue, formatValueFull } from "../format"
import { type MarketSection, type RowKind, SERIES_SLOTS, seriesColor } from "../types"

interface Props {
  markets: MarketSection[]
  rows: RowKind
  displayCurrency: string | null
  rateDate: string | null
  /** "1 USD = ₫25,999" per converted currency, printed with the total. */
  rateLines: string[]
  title: string
  subtitle: string
}

interface Series {
  key: string
  name: string
  color: string
  stackId: string
}

const OTHER_KEY = "__other"
// Markets after the first reuse the channel palette at a lighter mix, so two
// stacked bars side by side stay tellable apart without a second palette.
const MARKET_TINT = [100, 55, 35]

function tint(color: string, marketIndex: number): string {
  const pct = MARKET_TINT[Math.min(marketIndex, MARKET_TINT.length - 1)]
  return pct === 100 ? color : `color-mix(in srgb, ${color} ${pct}%, var(--card))`
}

// Each market is one stacked bar per period (its own stackId). Channel rows
// become the segments (fixed color slots, the rest fold into "Other");
// product rows chart as the market's total, like the market page.
function buildSeries(markets: MarketSection[], rows: RowKind): Series[] {
  return markets.flatMap((m, mi) => {
    const stacked = rows === "channels" && m.pivot.rows.length > 1
    if (!stacked) return [{ key: `${m.id}:total`, name: m.name, color: tint(seriesColor(0), mi), stackId: m.id }]
    const named = m.pivot.rows.slice(0, SERIES_SLOTS).map((r, i) => ({
      key: `${m.id}:${r.key}`, name: `${m.name} · ${r.label}`, color: tint(seriesColor(i), mi), stackId: m.id,
    }))
    if (m.pivot.rows.length > SERIES_SLOTS) {
      named.push({ key: `${m.id}:${OTHER_KEY}`, name: `${m.name} · Other`, color: tint(seriesColor(SERIES_SLOTS), mi), stackId: m.id })
    }
    return named
  })
}

function buildData(markets: MarketSection[], rows: RowKind, columns: { key: string; label: string }[]) {
  return columns.map((col) => {
    const point: Record<string, string | number> = { label: col.label }
    markets.forEach((m) => {
      const stacked = rows === "channels" && m.pivot.rows.length > 1
      if (!stacked) {
        point[`${m.id}:total`] = m.pivot.totals[col.key]?.display ?? 0
        return
      }
      let other = 0
      m.pivot.rows.forEach((row, i) => {
        const v = row.cells[col.key]?.display ?? 0
        if (i < SERIES_SLOTS) point[`${m.id}:${row.key}`] = v
        else other += v
      })
      if (m.pivot.rows.length > SERIES_SLOTS) point[`${m.id}:${OTHER_KEY}`] = other
    })
    return point
  })
}

export default function OverviewChart({ markets, rows, displayCurrency, rateDate, rateLines, title, subtitle }: Props) {
  const shown = markets.filter((m) => m.converted)
  const hidden = markets.filter((m) => !m.converted)
  const columns = markets[0]?.pivot.columns ?? []
  const series = buildSeries(shown, rows)
  const data = buildData(shown, rows, columns)
  const seriesIndex = Object.fromEntries(series.map((s, i) => [s.key, i]))
  // Top segment of each market's bar gets the rounded corners.
  const lastOfStack = new Set(series.map((s) => series.filter((o) => o.stackId === s.stackId).slice(-1)[0]?.key))
  const compact = (v: number) => formatValue(v, displayCurrency)
  const full = (v: number) => formatValueFull(v, displayCurrency)
  // The only cross-market number on the page: every market's total in the
  // display currency. Blank while any market still lacks a rate.
  const total = hidden.length === 0 ? shown.reduce((sum, m) => sum + (m.pivot.grand_total.display ?? 0), 0) : null

  return (
    <Card className="px-5 pb-3 pt-4">
      <div className="mb-3 flex items-start justify-between">
        <div>
          <div className="text-sm font-semibold">{title}</div>
          <div className="text-xs text-muted-foreground">{subtitle}</div>
        </div>
        <div className="text-right">
          <div className="text-[11px] text-muted-foreground">
            {["Total revenue, all markets", ...rateLines, rateDate ? formatDate(rateDate) : null].filter(Boolean).join(" · ")}
          </div>
          <div className="text-2xl font-semibold tabular-nums tracking-tight">
            {total == null ? <span className="text-muted-foreground">–</span> : compact(total)}
          </div>
        </div>
      </div>

      {hidden.length > 0 && (
        <div className="mb-3 rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          {hidden.map((m) => m.name).join(", ")} not shown: no exchange rate for {hidden.map((m) => m.currency).join(", ")} yet.
          Rates refresh nightly.
        </div>
      )}

      {columns.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground">No days in the selected period.</div>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} barGap={2} barCategoryGap="25%">
            <CartesianGrid vertical={false} stroke="var(--chart-grid)" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "var(--chart-axis)" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
            <YAxis tickFormatter={compact} tick={{ fontSize: 11, fill: "var(--chart-axis)" }} axisLine={false} tickLine={false} width={64} />
            <Tooltip
              formatter={(v) => full(Number(v))}
              itemSorter={(item) => seriesIndex[String(item.dataKey)] ?? 0}
              cursor={{ fill: "var(--muted)" }}
              contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: "var(--border)" }}
            />
            {series.length > 1 && (
              <Legend wrapperStyle={{ fontSize: 12 }} iconType="square" iconSize={10} itemSorter={null} />
            )}
            {series.map((s) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.name}
                stackId={s.stackId}
                fill={s.color}
                stroke="var(--card)"
                strokeWidth={1}
                maxBarSize={24}
                radius={lastOfStack.has(s.key) ? [4, 4, 0, 0] : 0}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  )
}
